import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { RankingsService } from "../../../../../core/http/services/RankingsService";
import { RoundsService } from "../../../../../core/http/services/RoundsService";
import { MatchesService } from "../../../../../core/http/services/MatchesService";
import { CategoriesService } from "../../../../../core/http/services/CategoriesService";
import { PlayersService } from "../../../../../core/http/services/PlayersService";
import AuthContext from "@/contexts/AuthContext";
import { useParams } from "react-router-dom";
import { isCancelledMatch, isPendingMatch } from "@/core/utils/matches";
import { CORINGAS, isCoringaId } from "@/core/constants/coringas";

const initializeSeasonUiState = () => ({
  expanded: false,
  selectedRound: null,
  selectedCategory: "",
  searchPlayer: "",
});

const normalizeGroupPlayer = (player, fallbackId) => {
  const id = player?.id ?? player?.playerId ?? fallbackId ?? null;
  const name = player?.name ?? null;
  if (!id || !name) return null;

  return {
    id,
    name,
    isCoringa: Boolean(player?.isCoringa) || isCoringaId(id),
  };
};

const normalizeGroupRelationPlayer = (groupPlayer) =>
  normalizeGroupPlayer(groupPlayer?.player, groupPlayer?.playerId);

const getMatchPlayers = (match) =>
  [
    normalizeGroupPlayer(match?.player1, match?.player1Id),
    normalizeGroupPlayer(match?.player2, match?.player2Id),
    normalizeGroupPlayer(match?.player3, match?.player3Id),
    normalizeGroupPlayer(match?.player4, match?.player4Id),
  ].filter(Boolean);

const uniquePlayersById = (players = []) => {
  const map = new Map();
  players.forEach((player) => {
    if (!player?.id || map.has(player.id)) return;
    map.set(player.id, player);
  });
  return Array.from(map.values());
};

const getLinePlayers = (line) =>
  line?.players?.length
    ? uniquePlayersById(line.players)
    : uniquePlayersById((line?.games || []).flatMap((game) => game.players || []));

const getUsedCoringaIds = (line) =>
  new Set(
    [
      ...getLinePlayers(line)
        .filter((player) => player.isCoringa || isCoringaId(player.id))
        .map((player) => player.id),
      ...(line?.absences || [])
        .map((absence) => absence?.coringaId)
        .filter(Boolean),
    ],
  );

const getAvailableCoringas = (line) => {
  const usedCoringaIds = getUsedCoringaIds(line);
  return CORINGAS.filter((coringa) => !usedCoringaIds.has(coringa.id));
};

const getSubstitutablePlayers = (line) => {
  const absentPlayerIds = new Set(
    (line?.absences || [])
      .map((absence) => absence?.playerId)
      .filter(Boolean),
  );

  return getLinePlayers(line).filter(
    (player) =>
      !player.isCoringa && !isCoringaId(player.id) && !absentPlayerIds.has(player.id),
  );
};

const getReplacementPreview = (line, selectedPlayerIds = []) => {
  const availableCoringas = getAvailableCoringas(line);
  const playersById = new Map(
    getSubstitutablePlayers(line).map((player) => [player.id, player]),
  );

  return selectedPlayerIds
    .map((playerId, index) => {
      const player = playersById.get(playerId);
      const coringa = availableCoringas[index];
      if (!player || !coringa) return null;
      return {
        playerId: player.id,
        playerName: player.name,
        coringaId: coringa.id,
        coringaName: coringa.name,
      };
    })
    .filter(Boolean);
};

export default function useAdminMatches() {
  const { userLoggedData } = useContext(AuthContext);
  const { clubId: clubIdFromUrl } = useParams();
  const clubId = clubIdFromUrl || userLoggedData?.selectedClubId || null;

  const [seasons, setSeasons] = useState([]);
  const [seasonUi, setSeasonUi] = useState({});
  const seasonUiRef = useRef({});

  const [roundsByRankingId, setRoundsByRankingId] = useState({});
  const [seasonGamesResponse, setSeasonGamesResponse] = useState({});
  const [lineDrafts, setLineDrafts] = useState({});
  const [lineOriginalResults, setLineOriginalResults] = useState({});
  const [sentLines, setSentLines] = useState({});
  const isCancellingMatchesRef = useRef(false);
  const isSubstitutingCoringaRef = useRef(false);
  const isRemovingRankPlayerRef = useRef(false);
  const [cancelMatchesDialog, setCancelMatchesDialog] = useState({
    open: false,
    seasonId: null,
    roundId: null,
    line: null,
    loading: false,
    errorMessage: null,
  });
  const [substituteCoringaDialog, setSubstituteCoringaDialog] = useState({
    open: false,
    seasonId: null,
    roundId: null,
    line: null,
    selectedPlayerIds: [],
    loading: false,
    errorMessage: null,
  });
  const [removeRankPlayerDialog, setRemoveRankPlayerDialog] = useState({
    open: false,
    seasonId: null,
    player: null,
    loading: false,
    errorMessage: null,
  });

  useEffect(() => {
    seasonUiRef.current = seasonUi;
  }, [seasonUi]);

  const loadSeasonGames = useCallback(async (seasonId, roundId) => {
    if (!roundId) return;

    setSeasonGamesResponse((prev) => ({
      ...prev,
      [seasonId]: { ...(prev[seasonId] || {}), loading: true },
    }));

    const { status, data } = await RoundsService.getById(roundId);

    if (status !== 200) {
      setSeasonGamesResponse((prev) => ({
        ...prev,
        [seasonId]: { loading: false, payload: { groups: [] } },
      }));
      return;
    }

    const groups = (data?.groups ?? []).map((group) => {
      const games = (group.matches ?? []).map((match) => {
        const players = getMatchPlayers(match);
        return {
          gameId: match.id,
          teamA: [match.player1?.name, match.player2?.name].filter(Boolean),
          teamB: [match.player3?.name, match.player4?.name].filter(Boolean),
          players,
          playerIds: players.map((player) => player.id),
          finished: match.finished ?? false,
          status: match.status ?? null,
          isCancelled: isCancelledMatch(match),
          result: {
            teamA: match.team1Score ?? "",
            teamB: match.team2Score ?? "",
          },
        };
      });

      return {
        lineId: group.id,
        categoryId: group.categoryId,
        absences: Array.isArray(group.absences) ? group.absences : [],
        players: uniquePlayersById(
          Array.isArray(group.players)
            ? group.players.map(normalizeGroupRelationPlayer).filter(Boolean)
            : [],
        ),
        games,
      };
    });

    setSeasonGamesResponse((prev) => ({
      ...prev,
      [seasonId]: { loading: false, payload: { groups } },
    }));

    // Ao carregar uma rodada, sincroniza "originais" e drafts com o que veio da API.
    // Isso evita comparar/mandar PATCH para jogos que o usuário não alterou (ex.: draft antigo).
    setLineOriginalResults((prev) => {
      const updated = { ...prev };
      groups.forEach((line) => {
        updated[line.lineId] = updated[line.lineId] || {};
        line.games.forEach((game) => {
          updated[line.lineId][game.gameId] = {
            teamA: String(game.result?.teamA ?? ""),
            teamB: String(game.result?.teamB ?? ""),
          };
        });
      });
      return updated;
    });

    setLineDrafts((prev) => {
      const updated = { ...prev };
      groups.forEach((line) => {
        updated[line.lineId] = {};
        line.games.forEach((game) => {
          updated[line.lineId][game.gameId] = {
            teamA: String(game.result?.teamA ?? ""),
            teamB: String(game.result?.teamB ?? ""),
          };
        });
      });
      return updated;
    });
  }, []);

  const fetchRoundsForRanking = useCallback(
    async (rankingId, { loadGames = false } = {}) => {
      if (!clubId) return;
      const { status, data } = await RankingsService.getRounds(clubId, rankingId);
      if (status !== 200) return;

      const rounds = data?.rounds ?? [];
      setRoundsByRankingId((prev) => ({ ...prev, [rankingId]: rounds }));

      const defaultRound = rounds[rounds.length - 1];
      if (!defaultRound) return;

      const currentUi = seasonUiRef.current[rankingId];
      const isCurrentValid = rounds.some((r) => r.id === currentUi?.selectedRound);
      const roundToLoad = isCurrentValid ? currentUi.selectedRound : defaultRound.id;

      if (!isCurrentValid) {
        setSeasonUi((prev) => {
          const current = prev[rankingId];
          if (!current) return prev;
          return { ...prev, [rankingId]: { ...current, selectedRound: defaultRound.id } };
        });
      }

      if (loadGames) {
        loadSeasonGames(rankingId, roundToLoad);
      }
    },
    [clubId, loadSeasonGames],
  );

  const fetchRankings = useCallback(async () => {
    if (!clubId) return;
    const { status, data } = await RankingsService.getAll(clubId);
    if (status !== 200) return;

    const mapped = (data ?? []).map((ranking) => ({
      id: ranking.id,
      name: ranking.name,
      isActive: ranking.isActive,
    }));

    setSeasons(mapped);
    setSeasonUi(() => {
      const state = {};
      mapped.forEach((season, index) => {
        state[season.id] = { ...initializeSeasonUiState(), expanded: index === 0 };
      });
      return state;
    });

    if (mapped.length > 0) {
      fetchRoundsForRanking(mapped[0].id, { loadGames: true });
    }
  }, [clubId, fetchRoundsForRanking]);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  const [categories, setCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    if (!clubId) return;
    const { status, data } = await CategoriesService.get(clubId);
    if (status === 200) setCategories(data ?? []);
  }, [clubId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const [manageRankModal, setManageRankModal] = useState({
    open: false,
    seasonId: null,
  });
  const [manageRankSearch, setManageRankSearch] = useState("");
  const [manageRankPlayers, setManageRankPlayers] = useState([]);
  const [manageRankPlayerCategory, setManageRankPlayerCategory] = useState({});

  const fetchManageRankPlayers = useCallback(async (rankingId) => {
    if (!rankingId) return;
    if (!clubId) return;
    const { status, data } = await PlayersService.getAll(clubId, {
      rankingId,
      limit: 100,
    });
    if (status === 200) {
      const list = Array.isArray(data) ? data : (data?.data ?? data?.players ?? data?.items ?? []);
      setManageRankPlayers(list);
    }
  }, [clubId]);

  useEffect(() => {
    if (!manageRankModal.open) return;
    fetchManageRankPlayers(manageRankModal.seasonId);
  }, [manageRankModal.open, manageRankModal.seasonId, fetchManageRankPlayers]);

  const [addSeasonOpen, setAddSeasonOpen] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState("");

  const [finishModal, setFinishModal] = useState({ open: false, seasonId: null });
  const [newRoundModal, setNewRoundModal] = useState({
    open: false,
    seasonId: null,
    status: null,
    errorMessage: null,
    endsAt: null,
  });

  const selectedSeasonForFinish = useMemo(
    () => seasons.find((season) => season.id === finishModal.seasonId),
    [finishModal.seasonId, seasons],
  );

  const selectedSeasonForNewRound = useMemo(
    () => seasons.find((season) => season.id === newRoundModal.seasonId),
    [newRoundModal.seasonId, seasons],
  );

  const selectedSeasonForManageRank = useMemo(
    () => seasons.find((season) => season.id === manageRankModal.seasonId),
    [manageRankModal.seasonId, seasons],
  );

  const updateSeasonUi = useCallback(
    (seasonId, field, value) => {
      setSeasonUi((prev) => ({
        ...prev,
        [seasonId]: { ...prev[seasonId], [field]: value },
      }));

      if (field === "selectedRound") {
        loadSeasonGames(seasonId, value);
      }
    },
    [loadSeasonGames],
  );

  const handleToggleSeason = useCallback(
    (seasonId, expanded) => {
      setSeasonUi((prev) => ({
        ...prev,
        [seasonId]: { ...prev[seasonId], expanded },
      }));

      if (expanded) {
        fetchRoundsForRanking(seasonId, { loadGames: true });
      }
    },
    [fetchRoundsForRanking],
  );

  const isLineGameCancelled = useCallback(
    (lineId, matchId) =>
      Object.values(seasonGamesResponse).some((response) =>
        (response?.payload?.groups || []).some(
          (line) =>
            line.lineId === lineId &&
            (line.games || []).some(
              (game) => game.gameId === matchId && isCancelledMatch(game),
            ),
        ),
      ),
    [seasonGamesResponse],
  );

  const handleScoreChange = useCallback((lineId, matchId, teamField, value) => {
    if (isLineGameCancelled(lineId, matchId)) return;
    if (value !== "" && !/^\d+$/.test(value)) return;

    setLineDrafts((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        [matchId]: { ...prev[lineId]?.[matchId], [teamField]: value },
      },
    }));
  }, [isLineGameCancelled]);

  const canCancelGroup = useCallback((line) => {
    const games = line?.games || [];
    return games.length > 0 && games.every((game) => isPendingMatch(game));
  }, []);

  const getCurrentLine = useCallback(
    (seasonId, lineId) =>
      (seasonGamesResponse[seasonId]?.payload?.groups || []).find(
        (line) => line.lineId === lineId,
      ) || null,
    [seasonGamesResponse],
  );

  const openCancelMatchesDialog = useCallback(
    (seasonId, roundId, line) => {
      if (!canCancelGroup(line)) return;
      setCancelMatchesDialog({
        open: true,
        seasonId,
        roundId,
        line,
        loading: false,
        errorMessage: null,
      });
    },
    [canCancelGroup],
  );

  const closeCancelMatchesDialog = useCallback(() => {
    setCancelMatchesDialog((prev) => {
      if (prev.loading) return prev;
      return {
        open: false,
        seasonId: null,
        roundId: null,
        line: null,
        loading: false,
        errorMessage: null,
      };
    });
  }, []);

  const confirmCancelMatches = useCallback(async () => {
    if (isCancellingMatchesRef.current) return null;

    const { seasonId, roundId, line } = cancelMatchesDialog;
    const games = line?.games || [];

    if (!seasonId || !roundId || games.length === 0) {
      setCancelMatchesDialog((prev) => ({
        ...prev,
        errorMessage: "Grupo de partidas inválido.",
      }));
      return { severity: "error", message: "Grupo de partidas inválido." };
    }

    if (!canCancelGroup(line)) {
      setCancelMatchesDialog((prev) => ({
        ...prev,
        loading: true,
        errorMessage: null,
      }));
      await loadSeasonGames(seasonId, roundId);
      setCancelMatchesDialog({
        open: false,
        seasonId: null,
        roundId: null,
        line: null,
        loading: false,
        errorMessage: null,
      });
      return {
        severity: "error",
        message: "As partidas deste grupo não estão mais disponíveis para cancelamento.",
      };
    }

    isCancellingMatchesRef.current = true;

    setCancelMatchesDialog((prev) => ({
      ...prev,
      loading: true,
      errorMessage: null,
    }));

    try {
      const results = await Promise.allSettled(
        games.map((game) => MatchesService.cancel(game.gameId)),
      );
      const successCount = results.filter((result) => {
        if (result.status !== "fulfilled") return false;
        return result.value?.status === 200 || result.value?.status === 204;
      }).length;

      await loadSeasonGames(seasonId, roundId);

      setCancelMatchesDialog({
        open: false,
        seasonId: null,
        roundId: null,
        line: null,
        loading: false,
        errorMessage: null,
      });

      if (successCount === games.length) {
        return {
          severity: "success",
          message: "Partidas canceladas com sucesso.",
        };
      }

      if (successCount > 0) {
        return {
          severity: "warning",
          message: "Algumas partidas não puderam ser canceladas.",
        };
      }

      return {
        severity: "error",
        message: "Não foi possível cancelar as partidas.",
      };
    } finally {
      isCancellingMatchesRef.current = false;
    }
  }, [canCancelGroup, cancelMatchesDialog, loadSeasonGames]);

  const openSubstituteCoringaDialog = useCallback(
    (seasonId, roundId, line) => {
      if (!canCancelGroup(line)) return;

      setSubstituteCoringaDialog({
        open: true,
        seasonId,
        roundId,
        line,
        selectedPlayerIds: [],
        loading: false,
        errorMessage: null,
      });
    },
    [canCancelGroup],
  );

  const closeSubstituteCoringaDialog = useCallback(() => {
    setSubstituteCoringaDialog((prev) => {
      if (prev.loading) return prev;
      return {
        open: false,
        seasonId: null,
        roundId: null,
        line: null,
        selectedPlayerIds: [],
        loading: false,
        errorMessage: null,
      };
    });
  }, []);

  const toggleSubstitutePlayer = useCallback((playerId) => {
    setSubstituteCoringaDialog((prev) => {
      if (prev.loading) return prev;
      const selectedPlayerIds = prev.selectedPlayerIds || [];
      const alreadySelected = selectedPlayerIds.includes(playerId);

      if (alreadySelected) {
        return {
          ...prev,
          selectedPlayerIds: selectedPlayerIds.filter((id) => id !== playerId),
          errorMessage: null,
        };
      }

      const availableCount = getAvailableCoringas(prev.line).length;
      if (selectedPlayerIds.length >= availableCount) {
        return {
          ...prev,
          errorMessage: `Selecione no máximo ${availableCount} ${availableCount === 1 ? "jogador" : "jogadores"}.`,
        };
      }

      return {
        ...prev,
        selectedPlayerIds: [...selectedPlayerIds, playerId],
        errorMessage: null,
      };
    });
  }, []);

  const getSubstituteCoringaView = useCallback(() => {
    const line = substituteCoringaDialog.line;
    const selectedPlayerIds = substituteCoringaDialog.selectedPlayerIds || [];

    return {
      players: getSubstitutablePlayers(line),
      availableCoringas: getAvailableCoringas(line),
      selectedPlayerIds,
      replacementPreview: getReplacementPreview(line, selectedPlayerIds),
    };
  }, [substituteCoringaDialog.line, substituteCoringaDialog.selectedPlayerIds]);

  const confirmSubstituteWithCoringa = useCallback(async () => {
    if (isSubstitutingCoringaRef.current) return null;

    const { seasonId, roundId, line, selectedPlayerIds } = substituteCoringaDialog;
    const currentLine = line?.lineId ? getCurrentLine(seasonId, line.lineId) || line : line;
    const selectedIds = selectedPlayerIds || [];

    const fail = (message) => {
      setSubstituteCoringaDialog((prev) => ({ ...prev, errorMessage: message }));
      return { severity: "error", message };
    };

    if (!seasonId || !roundId || !currentLine?.games?.length) {
      return fail("Grupo de partidas inválido.");
    }

    if (!canCancelGroup(currentLine)) {
      setSubstituteCoringaDialog((prev) => ({
        ...prev,
        loading: true,
        errorMessage: null,
      }));
      await loadSeasonGames(seasonId, roundId);
      setSubstituteCoringaDialog({
        open: false,
        seasonId: null,
        roundId: null,
        line: null,
        selectedPlayerIds: [],
        loading: false,
        errorMessage: null,
      });
      return {
        severity: "error",
        message: "As partidas deste grupo não estão mais disponíveis para substituição.",
      };
    }

    if (selectedIds.length === 0) {
      return fail("Selecione ao menos um jogador para substituir.");
    }

    const substitutablePlayers = getSubstitutablePlayers(currentLine);
    const playersById = new Map(substitutablePlayers.map((player) => [player.id, player]));
    const hasInvalidPlayer = selectedIds.some((playerId) => {
      const player = playersById.get(playerId);
      return !player || player.isCoringa || isCoringaId(playerId);
    });

    if (hasInvalidPlayer) {
      return fail("A seleção contém jogador inválido para substituição.");
    }

    const availableCoringas = getAvailableCoringas(currentLine);
    if (selectedIds.length > availableCoringas.length) {
      return fail("A quantidade selecionada ultrapassa os coringas disponíveis.");
    }

    const replacements = getReplacementPreview(currentLine, selectedIds);
    if (replacements.length !== selectedIds.length) {
      return fail("Não foi possível montar as substituições.");
    }

    const replacementCoringaIds = replacements.map((item) => item.coringaId);
    if (new Set(replacementCoringaIds).size !== replacementCoringaIds.length) {
      return fail("Um mesmo coringa não pode ser atribuído a mais de um jogador.");
    }

    const usedCoringaIds = getUsedCoringaIds(currentLine);
    if (replacementCoringaIds.some((coringaId) => usedCoringaIds.has(coringaId))) {
      return fail("A seleção contém coringa já utilizado neste grupo.");
    }

    const operations = replacements
      .map((replacement) => {
        const game = currentLine.games.find(
          (item) =>
            isPendingMatch(item) && (item.playerIds || []).includes(replacement.playerId),
        );

        if (!game) return null;

        return {
          matchId: game.gameId,
          playerId: replacement.playerId,
          coringaId: replacement.coringaId,
        };
      })
      .filter(Boolean);

    if (operations.length === 0) {
      return fail("Nenhuma partida encontrada para os jogadores selecionados.");
    }

    const operationPlayerIds = new Set(operations.map((operation) => operation.playerId));
    if (selectedIds.some((playerId) => !operationPlayerIds.has(playerId))) {
      return fail("Algum jogador selecionado não possui partida neste grupo.");
    }

    isSubstitutingCoringaRef.current = true;

    setSubstituteCoringaDialog((prev) => ({
      ...prev,
      loading: true,
      errorMessage: null,
    }));

    try {
      const results = await Promise.allSettled(
        operations.map((operation) =>
          MatchesService.substituteWithCoringa(operation.matchId, {
            playerId: operation.playerId,
            coringaId: operation.coringaId,
          }),
        ),
      );
      const successCount = results.filter((result) => {
        if (result.status !== "fulfilled") return false;
        return result.value?.status === 200 || result.value?.status === 204;
      }).length;

      await loadSeasonGames(seasonId, roundId);

      setSubstituteCoringaDialog({
        open: false,
        seasonId: null,
        roundId: null,
        line: null,
        selectedPlayerIds: [],
        loading: false,
        errorMessage: null,
      });

      if (successCount === operations.length) {
        return {
          severity: "success",
          message: "Jogadores substituídos com sucesso.",
        };
      }

      if (successCount > 0) {
        return {
          severity: "warning",
          message: "Algumas substituições não puderam ser concluídas.",
        };
      }

      return {
        severity: "error",
        message: "Não foi possível substituir os jogadores.",
      };
    } finally {
      isSubstitutingCoringaRef.current = false;
    }
  }, [
    canCancelGroup,
    getCurrentLine,
    loadSeasonGames,
    substituteCoringaDialog,
  ]);

  const canSendLine = useCallback(
    (lineId, line) => {
      const validGames = line.games.filter((game) => !isCancelledMatch(game));
      return validGames.length > 0 && validGames.every((game) => {
        const draft = lineDrafts[lineId]?.[game.gameId];
        return Boolean(draft?.teamA?.trim() && draft?.teamB?.trim());
      });
    },
    [lineDrafts],
  );

  const lineHasChanges = useCallback(
    (lineId, line) =>
      line.games.some((game) => {
        if (isCancelledMatch(game)) return false;
        const draft = lineDrafts[lineId]?.[game.gameId];
        const original = lineOriginalResults[lineId]?.[game.gameId];
        if (!draft) return false;
        return (
          String(original?.teamA ?? "") !== draft.teamA ||
          String(original?.teamB ?? "") !== draft.teamB
        );
      }),
    [lineDrafts, lineOriginalResults],
  );

  const handleSendLine = useCallback(
    async (lineId, line) => {
      if (!canSendLine(lineId, line) || !lineHasChanges(lineId, line)) return null;

      const changedGames = line.games.filter((game) => {
        if (isCancelledMatch(game)) return false;
        const draft = lineDrafts[lineId]?.[game.gameId];
        const original = lineOriginalResults[lineId]?.[game.gameId];
        if (!draft) return false;
        return (
          String(original?.teamA ?? "") !== draft.teamA ||
          String(original?.teamB ?? "") !== draft.teamB
        );
      });

      for (const game of changedGames) {
        const draft = lineDrafts[lineId]?.[game.gameId];
        const result = await MatchesService.updateResult(game.gameId, {
          team1Score: Number(draft.teamA),
          team2Score: Number(draft.teamB),
        });

        if (result.status !== 200 && result.status !== 201) {
          return {
            error:
              result.data?.message ||
              result.message ||
              "Erro ao enviar resultados.",
          };
        }
      }

      setSeasonGamesResponse((prev) => {
        const updated = { ...prev };

        Object.keys(updated).forEach((seasonId) => {
          const payload = updated[seasonId]?.payload;
          if (!payload?.groups) return;

          updated[seasonId] = {
            ...updated[seasonId],
            payload: {
              ...payload,
              groups: payload.groups.map((existingLine) => {
                if (existingLine.lineId !== lineId) return existingLine;
                return {
                  ...existingLine,
                  games: existingLine.games.map((game) => {
                    if (isCancelledMatch(game)) return game;
                    const draft = lineDrafts[lineId]?.[game.gameId];
                    if (!draft) return game;
                    return {
                      ...game,
                      result: {
                        teamA: Number(draft.teamA),
                        teamB: Number(draft.teamB),
                      },
                    };
                  }),
                };
              }),
            },
          };
        });

        return updated;
      });

      setLineOriginalResults((prev) => {
        const original = { ...prev };
        original[lineId] = original[lineId] || {};
        line.games.forEach((game) => {
          if (isCancelledMatch(game)) return;
          const draft = lineDrafts[lineId]?.[game.gameId];
          if (!draft) return;
          original[lineId][game.gameId] = { teamA: draft.teamA, teamB: draft.teamB };
        });
        return original;
      });

      setSentLines((prev) => ({ ...prev, [lineId]: true }));
    },
    [canSendLine, lineDrafts, lineHasChanges, lineOriginalResults],
  );

  const handleAddSeason = useCallback(async () => {
    if (!newSeasonName.trim()) return;
    if (!clubId) return;

    const { status } = await RankingsService.create(clubId, {
      name: newSeasonName.trim(),
      year: new Date().getFullYear(),
    });

    if (status !== 200 && status !== 201) return;

    setNewSeasonName("");
    setAddSeasonOpen(false);
    fetchRankings();
  }, [clubId, newSeasonName, fetchRankings]);

  const handleFinishSeason = useCallback(async () => {
    const seasonId = finishModal.seasonId;
    if (!seasonId) return;
    if (!clubId) return;

    const { status } = await RankingsService.update(clubId, seasonId, { isActive: false });
    if (status !== 200 && status !== 201) return;

    setFinishModal({ open: false, seasonId: null });
    fetchRankings();
  }, [clubId, finishModal.seasonId, fetchRankings]);

  const handleCreateNewRound = useCallback(async () => {
    const seasonId = newRoundModal.seasonId;
    if (!seasonId) return;
    if (!clubId) return;

    setNewRoundModal((prev) => ({ ...prev, status: "loading" }));

    const { status: createStatus, data } = await RankingsService.createRound(
      clubId,
      seasonId,
      { endsAt: newRoundModal.endsAt },
    );
    if (createStatus !== 200 && createStatus !== 201) {
      setNewRoundModal((prev) => ({ ...prev, status: "error", errorMessage: data?.message ?? null }));
      return;
    }

    const { status: genStatus, data: genData } = await RoundsService.generate(data.id);
    if (genStatus !== 200 && genStatus !== 201) {
      setNewRoundModal((prev) => ({ ...prev, status: "error", errorMessage: genData?.message ?? null }));
      return;
    }

    setNewRoundModal((prev) => ({ ...prev, status: "success" }));
    fetchRoundsForRanking(seasonId);
    setSeasonUi((prev) => {
      const current = prev[seasonId];
      if (!current) return prev;
      return { ...prev, [seasonId]: { ...current, selectedRound: data.id } };
    });
    loadSeasonGames(seasonId, data.id);
  }, [clubId, newRoundModal.seasonId, newRoundModal.endsAt, fetchRoundsForRanking, loadSeasonGames]);

  const handleOpenManageRank = useCallback(
    (seasonId) => {
      setManageRankSearch("");
      setManageRankModal({ open: true, seasonId });
      setManageRankPlayerCategory({});
    },
    [],
  );

  const handleCloseManageRank = useCallback(() => {
    setManageRankModal({ open: false, seasonId: null });
    setManageRankSearch("");
    setManageRankPlayers([]);
    setManageRankPlayerCategory({});
    setRemoveRankPlayerDialog({
      open: false,
      seasonId: null,
      player: null,
      loading: false,
      errorMessage: null,
    });
  }, []);

  const handleManageRankUserCategoryChange = useCallback((userId, categoryId) => {
    setManageRankPlayerCategory((prev) => ({ ...prev, [userId]: categoryId }));
  }, []);

  const handleAddUserToRank = useCallback(
    async (seasonId, userId, categoryIdOverride) => {
      if (!clubId) return;
      const categoryId =
        categoryIdOverride ||
        manageRankPlayerCategory[userId];
      if (!categoryId) return;
      const { status } = await RankingsService.addPlayers(clubId, seasonId, {
        players: [{ playerId: userId, categoryId }],
      });
      if (status === 200 || status === 201) {
        fetchManageRankPlayers(seasonId);
      }
    },
    [clubId, fetchManageRankPlayers, manageRankPlayerCategory],
  );

  const handleRemoveUserFromRank = useCallback((seasonId, user) => {
    if (!seasonId || !user?.id) return;
    setRemoveRankPlayerDialog({
      open: true,
      seasonId,
      player: user,
      loading: false,
      errorMessage: null,
    });
  }, []);

  const closeRemoveRankPlayerDialog = useCallback(() => {
    setRemoveRankPlayerDialog((prev) => {
      if (prev.loading) return prev;
      return {
        open: false,
        seasonId: null,
        player: null,
        loading: false,
        errorMessage: null,
      };
    });
  }, []);

  const confirmRemoveUserFromRank = useCallback(async () => {
    if (isRemovingRankPlayerRef.current) return;

    const { seasonId, player } = removeRankPlayerDialog;
    if (!clubId || !seasonId || !player?.id) {
      setRemoveRankPlayerDialog((prev) => ({
        ...prev,
        errorMessage: "Jogador inválido para remoção.",
      }));
      return;
    }

    isRemovingRankPlayerRef.current = true;
    setRemoveRankPlayerDialog((prev) => ({
      ...prev,
      loading: true,
      errorMessage: null,
    }));

    try {
      const result = await RankingsService.removePlayer(clubId, seasonId, player.id);
      if (result.status === 204 || result.status === 200) {
        await fetchManageRankPlayers(seasonId);
        setRemoveRankPlayerDialog({
          open: false,
          seasonId: null,
          player: null,
          loading: false,
          errorMessage: null,
        });
        return;
      }

      setRemoveRankPlayerDialog((prev) => ({
        ...prev,
        loading: false,
        errorMessage:
          result.data?.message ||
          result.message ||
          "Não foi possível remover o jogador do ranking.",
      }));
    } finally {
      isRemovingRankPlayerRef.current = false;
    }
  }, [clubId, fetchManageRankPlayers, removeRankPlayerDialog]);

  const getSeasonView = useCallback(
    (season) => {
      const ui = seasonUi[season.id] || initializeSeasonUiState();
      const seasonResult = seasonGamesResponse[season.id] || {
        loading: false,
        payload: { groups: [] },
      };

      const allGroups = seasonResult.payload?.groups || [];
      const categoryFiltered = ui.selectedCategory
        ? allGroups.filter((g) => g.categoryId === ui.selectedCategory)
        : allGroups;

      const query = (ui.searchPlayer || "").trim().toLowerCase();
      const lines = !query
        ? categoryFiltered
        : categoryFiltered.filter((line) =>
            line.games.some(
              (game) =>
                game.teamA.some((name) => name.toLowerCase().includes(query)) ||
                game.teamB.some((name) => name.toLowerCase().includes(query)),
            ),
          );

      const rounds = roundsByRankingId[season.id] ?? [];

      return { ui, seasonResult, lines, membersCount: 0, rounds };
    },
    [roundsByRankingId, seasonGamesResponse, seasonUi],
  );

  const getManageRankView = useCallback(() => {
    const seasonId = manageRankModal.seasonId;
    if (!seasonId) return { seasonId: null, usersInRank: [], usersOutRank: [] };

    const query = manageRankSearch.trim().toLowerCase();
    const matchesQuery = (p) => !query || p.name.toLowerCase().includes(query);

    const usersInRank = manageRankPlayers.filter((p) => p.inRanking).filter(matchesQuery);
    const usersOutRank = manageRankPlayers.filter((p) => !p.inRanking).filter(matchesQuery);

    return { seasonId, usersInRank, usersOutRank };
  }, [manageRankModal.seasonId, manageRankPlayers, manageRankSearch]);

  return {
    seasons,
    categories,
    seasonUi,
    lineDrafts,
    sentLines,
    cancelMatchesDialog,
    substituteCoringaDialog,
    removeRankPlayerDialog,
    addSeasonOpen,
    newSeasonName,
    finishModal,
    newRoundModal,
    manageRankModal,
    manageRankSearch,
    selectedSeasonForFinish,
    selectedSeasonForNewRound,
    selectedSeasonForManageRank,
    setAddSeasonOpen,
    setNewSeasonName,
    setFinishModal,
    setNewRoundModal,
    setManageRankSearch,
    canCancelGroup,
    openCancelMatchesDialog,
    closeCancelMatchesDialog,
    confirmCancelMatches,
    openSubstituteCoringaDialog,
    closeSubstituteCoringaDialog,
    toggleSubstitutePlayer,
    confirmSubstituteWithCoringa,
    closeRemoveRankPlayerDialog,
    confirmRemoveUserFromRank,
    getSubstituteCoringaView,
    getSeasonView,
    updateSeasonUi,
    handleToggleSeason,
    handleScoreChange,
    lineHasChanges,
    canSendLine,
    handleSendLine,
    handleAddSeason,
    handleFinishSeason,
    handleCreateNewRound,
    handleOpenManageRank,
    handleCloseManageRank,
    handleManageRankUserCategoryChange,
    manageRankPlayerCategory,
    handleAddUserToRank,
    handleRemoveUserFromRank,
    getManageRankView,
  };
}
